import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Center,
  Spinner,
  Button,
  FormControl,
  FormLabel,
  Icon,
  Checkbox,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon } from "@chakra-ui/icons";
import { BsRobot } from "react-icons/bs";
import { Header } from "build-ts/metadata";
import { UserProto } from "build-ts/interface";
import { deleteAnalyst, cloneAnalyst } from "services/AnalystService";
import { cloneAnalystMemoryFacts } from "services/MemoryFactService";
import { HeaderWithStats, updateHeader } from "services/MetadataServices";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import { updateUser } from "services/UserService";
import { useDisclosure } from "@chakra-ui/react";
import RenameModal from "common/modals/RenameModal";
import ConfirmationModal from "common/modals/ConfirmationModal";
import DuplicateModal from "common/modals/DuplicateModal";
import AnalystSection from "../components/AnalystSection";

export interface AnalystListProps {
  analystHeaders: HeaderWithStats[];
  setAnalystHeaders: React.Dispatch<React.SetStateAction<HeaderWithStats[]>>;
  onAnalystSelection: (header: HeaderWithStats) => void;
  onCreateNewAnalyst: () => void;
  /** True while the initial card list (no stats) is still loading — shows a full-page spinner. */
  isLoading?: boolean;
  /** True while the stats are being fetched — shows per-card stat spinners. */
  isLoadingStats?: boolean;
}

const AnalystList: React.FC<AnalystListProps> = ({
  analystHeaders,
  setAnalystHeaders,
  onAnalystSelection,
  onCreateNewAnalyst,
  isLoading,
  isLoadingStats,
}) => {
  const { user, userId, defaultAnalystId, setDefaultAnalystId } = useUser();

  const [searchTerm, setSearchTerm] = useState("");

  // Rename modal state
  const [editingAnalyst, setEditingAnalyst] = useState<Header | null>(null);
  const [newAnalystName, setNewAnalystName] = useState("");

  // Delete modal state
  const [deletingAnalyst, setDeletingAnalyst] = useState<Header | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Duplicate modal state
  const [duplicatingAnalyst, setDuplicatingAnalyst] = useState<Header | null>(null);
  const [duplicateAnalystName, setDuplicateAnalystName] = useState("");
  const [copyWorkflows, setCopyWorkflows] = useState(true);
  const [copyMemory, setCopyMemory] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isDuplicateOpen, onOpen: onDuplicateOpen, onClose: onDuplicateClose } = useDisclosure();

  const filteredHeaders = useMemo(() => {
    if (!searchTerm) return analystHeaders;
    return analystHeaders.filter((h) =>
      h?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [analystHeaders, searchTerm]);

  const handleRenameClick = useCallback((header: Header) => {
    setEditingAnalyst(header);
    setNewAnalystName(header.name || "");
    onRenameOpen();
  }, [onRenameOpen]);

  const handleDeleteClick = useCallback((header: Header) => {
    setDeletingAnalyst(header);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleDuplicateClick = useCallback((header: Header) => {
    if (!header?.id) return;
    setDuplicatingAnalyst(header);
    setDuplicateAnalystName(`${header.name || ""} (Copy)`);
    setCopyWorkflows(true);
    setCopyMemory(false);
    onDuplicateOpen();
  }, [onDuplicateOpen]);

  const handleConfirmRename = async () => {
    if (!editingAnalyst || !newAnalystName.trim() || !editingAnalyst.id || !editingAnalyst.type)
      return;

    try {
      const updatedHeader = await updateHeader(
        editingAnalyst.id,
        newAnalystName.trim(),
        editingAnalyst.description || "",
        editingAnalyst.type
      );

      if (updatedHeader) {
        setAnalystHeaders((prev) =>
          prev.map((h) =>
            h?.id === editingAnalyst?.id ? { ...h, name: newAnalystName.trim() } : h
          )
        );
        showToast("Analyst renamed successfully", "", "success");
      }
    } catch {
      showToast("Failed to rename analyst", "", "error");
    } finally {
      onRenameClose();
      setEditingAnalyst(null);
      setNewAnalystName("");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingAnalyst?.id) return;

    setIsDeleting(true);
    try {
      await deleteAnalyst(deletingAnalyst.id);
      setAnalystHeaders((prev) => prev.filter((h) => h.id !== deletingAnalyst.id));
      showToast("Analyst deleted successfully", "", "success");
    } catch {
      showToast("Failed to delete analyst", "", "error");
    } finally {
      setIsDeleting(false);
      setDeletingAnalyst(null);
      onDeleteClose();
    }
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicatingAnalyst?.id || !duplicateAnalystName.trim()) return;

    try {
      setIsDuplicating(true);
      const clonedAnalyst = await cloneAnalyst(
        duplicatingAnalyst.id,
        duplicateAnalystName.trim(),
        copyWorkflows,
        true   // save
      );

      if (clonedAnalyst?.header) {
        if (copyMemory) {
          await cloneAnalystMemoryFacts(duplicatingAnalyst.id, clonedAnalyst.header.id!);
        }
        setAnalystHeaders((prev) => [clonedAnalyst.header!, ...prev]);
        if (onAnalystSelection) {
          onAnalystSelection(clonedAnalyst.header);
        }
      }
    } finally {
      setIsDuplicating(false);
      setDuplicatingAnalyst(null);
      onDuplicateClose();
    }
  };

  const handleUpdateDefaultAnalyst = useCallback(async (analystHeader: Header) => {
    const isCurrentlyDefault = defaultAnalystId === analystHeader.id;
    const newDefaultAnalystId = isCurrentlyDefault ? "" : analystHeader.id || "";
    const clientObject = newDefaultAnalystId ? { defaultAnalystId: newDefaultAnalystId } : {};

    const userRequest = UserProto.create({
      header: user?.header,
      email: user?.email,
      is_admin: user?.is_admin,
      client_object_json: JSON.stringify(clientObject),
    });

    const updatedUser = await updateUser(userRequest);
    if (updatedUser) {
      setDefaultAnalystId(newDefaultAnalystId);
      if (newDefaultAnalystId) {
        showToast(`${analystHeader.name} set as primary analyst`, "", "success");
      } else {
        showToast("Primary analyst removed", "", "success");
      }
    }
  }, [defaultAnalystId, user, setDefaultAnalystId]);

  return (
    <>
      <Box height="100%" bg="white" overflowY="auto">
        <Box maxW="1400px" mx="auto" px={9} py={6}>
          <VStack spacing={4} align="stretch">
            {/* Header */}
            <Box>
              <Flex justify="space-between" align="center" mb={1} mt={3}>
                <Box>
                  <HStack spacing={5} mb={2}>
                    <Icon as={BsRobot} boxSize={9} color="purple.700" />
                    <Text
                      fontSize="2xl"
                      fontWeight="700"
                      color="gray.800"
                      letterSpacing="-0.5px"
                      mb={0}
                    >
                      Analysts
                    </Text>
                  </HStack>
                  <Text
                    fontSize="md"
                    color="gray.800"
                    mt={0}
                    align="left"
                    fontWeight="600"
                  >
                    Analysts are how you chat with Anko, each tailored with its own data, memory,
                    and workflows.
                  </Text>
                </Box>
                <Button
                  leftIcon={<AddIcon boxSize={4} />}
                  onClick={onCreateNewAnalyst}
                  size="lg"
                  bg="purple.600"
                  color="white"
                  px={6}
                  _hover={{
                    bg: "purple.700",
                    transform: "translateY(-1px)",
                    boxShadow: "lg",
                  }}
                  _active={{ bg: "purple.800", transform: "translateY(0)" }}
                  transition="all 0.2s"
                  fontWeight="600"
                  boxShadow="md"
                >
                  New Analyst
                </Button>
              </Flex>

              <Flex align="center" justify="space-between" gap={4} w="100%">
                <InputGroup size="lg" maxW="500px" flex="1" minW={0}>
                  <InputLeftElement pointerEvents="none" height="full">
                    <SearchIcon color="gray.400" boxSize={5} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search analysts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="gray.50"
                    border="1px"
                    borderColor="gray.200"
                    fontSize="md"
                    _hover={{ borderColor: "gray.300", bg: "white" }}
                    _focus={{
                      borderColor: "purple.400",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                      bg: "white",
                    }}
                    _placeholder={{ color: "gray.400" }}
                  />
                </InputGroup>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontWeight="600"
                  textAlign="right"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {"Showing "}
                  {filteredHeaders.length}{" "}
                  {filteredHeaders.length === 1 ? "analyst" : "analysts"}
                  {searchTerm && " found"}
                </Text>
              </Flex>
            </Box>

            {/* Analyst grid */}
            <Box>
              {isLoading ? (
                <Center py={9}>
                  <VStack spacing={4}>
                    <Spinner size="xl" color="purple.600" thickness="3px" />
                    <Text color="gray.600" fontSize="md">
                      Loading analysts...
                    </Text>
                  </VStack>
                </Center>
              ) : filteredHeaders.length === 0 ? (
                <Center py={9}>
                  <VStack spacing={3}>
                    <Box p={4} bg="gray.100" borderRadius="full">
                      <SearchIcon boxSize={8} color="gray.400" />
                    </Box>
                    <Text color="gray.600" fontSize="lg" fontWeight="500">
                      {searchTerm ? "No analysts found" : "No analysts yet"}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      {searchTerm
                        ? "Try adjusting your search"
                        : "Click 'New Analyst' to create your first analyst"}
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <AnalystSection
                  filteredHeaders={filteredHeaders}
                  userId={userId}
                  defaultAnalystId={defaultAnalystId}
                  onAnalystSelection={onAnalystSelection}
                  onRenameClick={handleRenameClick}
                  onDuplicateClick={handleDuplicateClick}
                  onDeleteClick={handleDeleteClick}
                  onUpdateDefaultAnalyst={handleUpdateDefaultAnalyst}
                  isLoading={isLoadingStats}
                />
              )}
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Rename Modal */}
      <RenameModal
        isOpen={isRenameOpen}
        onClose={onRenameClose}
        title="Rename Analyst"
        label="Analyst Name"
        placeholder="Enter new analyst name"
        name={newAnalystName}
        onNameChange={setNewAnalystName}
        onConfirm={handleConfirmRename}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleConfirmDelete}
        title="Delete Analyst"
        text={`Are you sure you want to delete "${deletingAnalyst?.name ?? ""}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
      />


      {/* Duplicate Modal */}
      <DuplicateModal
        isOpen={isDuplicateOpen}
        onClose={onDuplicateClose}
        title="Duplicate Analyst"
        nameLabel="Analyst Name"
        name={duplicateAnalystName}
        onNameChange={setDuplicateAnalystName}
        onConfirm={handleConfirmDuplicate}
        isLoading={isDuplicating}
      >
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
            Options
          </FormLabel>
          <VStack align="start" spacing={3}>
            <Checkbox
              isChecked={copyWorkflows}
              onChange={(e) => setCopyWorkflows(e.target.checked)}
              colorScheme="purple"
              size="md"
            >
              <Text fontSize="sm" m={0}>
                Copy Workflows
              </Text>
            </Checkbox>
            <Checkbox
              isChecked={copyMemory}
              onChange={(e) => setCopyMemory(e.target.checked)}
              colorScheme="purple"
              size="md"
            >
              <Text fontSize="sm" m={0}>
                Copy Memory
              </Text>
            </Checkbox>
          </VStack>
        </FormControl>
      </DuplicateModal>
    </>
  );
};

export default AnalystList; 
